import {
  Box,
  Card, CircularProgress,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography
} from '@mui/material'
import EditIcon from '@mui/icons-material/Edit'
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty'
import DeleteIcon from '@mui/icons-material/Delete'
import CloseIcon from '@mui/icons-material/Close'
import {
  AlertDialog,
  PersonAddressBlock,
  PersonAutocomplete,
  IconButtonWithTooltip,
  usePositionLuts,
  useGlobalMessageHandler, useCountriesLuts, useCustomBehaviourLuts
} from 'components'
import { CommentDialog } from 'components/common/comment-dialog'
import { internationalCommentService } from 'services/international/commentService'
import React, { useEffect, useState } from 'react'
import {
  createPositionService,
  type Person,
  type PersonAddress,
  type Position,
  type Region,
  updatePositionService
} from 'services'
import SaveIcon from '@mui/icons-material/Save'
import ArrowOutwardIcon from '@mui/icons-material/ArrowOutward'
import { formatErrorMessages } from 'interfaces'
import { deletePosition } from 'services'
import { retirePersonFromPosition } from 'services/position/retirePositionService'
import { getAddressFromOrg, isEmptyLastNameAllowed } from 'helpers'
import LastUpdatedComponent from '../../common/format-date'
import { useSessionContext } from 'session'

export interface PositionCardProps {
  index: number
  originalPosition: Position
  createCard: boolean
  clearPositionToAdd?: () => void
  triggerReload: () => Promise<void>
  displayOrgLink: boolean
}

export const PositionCard: React.FC<PositionCardProps> = ({ originalPosition, displayOrgLink, index, createCard, triggerReload, clearPositionToAdd = () => {} }) => {
  const [position, setPosition] = useState<Position>(originalPosition)
  const [disabled, setDisabled] = useState<boolean>(true)
  const [loading, setLoading] = useState<boolean>(false)
  const [editingCard, setEditingCard] = useState(createCard)
  const [createPersonMode, setPersonCreateMode] = useState(false)
  const [openDeleteDialog, setOpenDeleteDialog] = React.useState(false)
  const [openRetireDialog, setOpenRetireDialog] = React.useState(false)
  const { lutValues: positionLutValues, loading: loadingPositionLutvalues } = usePositionLuts()
  const { lutValues: customBehaviourValues } = useCustomBehaviourLuts()

  const { openNewTab } = useSessionContext()

  const { lutValues: countryLutValues } = useCountriesLuts()

  const { showSuccessMessage, showErrorMessage } = useGlobalMessageHandler()

  useEffect(() => {
    setPosition(originalPosition)
  }, [originalPosition])

  const handleSave = (): void => {
    setLoading(true)

    const primaryAddressFilled = isAllFieldsPrimaryFilled()
    const positionFieldsFilled = isfilledPositionFields()
    if (positionFieldsFilled.length > 0) {
      showErrorMessage(`Please fill in all position fields [${positionFieldsFilled.join(', ')}].`)
      setLoading(false)
      return
    }
    if (position.person !== null && primaryAddressFilled.length > 0) {
      showErrorMessage(`Please fill in all address fields [${primaryAddressFilled.join(', ')}].`)
      setLoading(false)
      return
    }

    if (position.address?.phone != null) {
      if (position.address.phone?.countryCode !== '' && position.address.phone?.phoneNumber !== '') {
        position.address.phone.phoneNumber = `(${position.address.phone.countryCode ?? ''}) ${position.address.phone.phoneNumber ?? ''}`
        position.address.phone.countryCode = ''
      }
    }

    swapAddress()

    if (position.id > 0) {
      void updatePositionService(originalPosition.organization.id, position).then((value) => {
        if (value.succeeded) {
          showSuccessMessage('Position Updated')
          clearPositionToAdd()
          void triggerReload()
        } else {
          showErrorMessage(formatErrorMessages(value.errors))
        }
      }).finally(() => { setLoading(false) }
      )
      return
    }

    if (createCard) {
      void createPositionService(originalPosition.organization.id, position).then((value) => {
        if (value.succeeded) {
          showSuccessMessage('Position Created')
          clearPositionToAdd()
          void triggerReload()
        } else {
          showErrorMessage(formatErrorMessages(value.errors))
        }
      }).finally(() => { setLoading(false) }
      )
    }
  }
  const swapAddress = (): void => {
    if (position?.address?.primaryAddress.mailingAddress === false) {
      [position.address.primaryAddress, position.address.secondaryAddress] = [position?.address.secondaryAddress, position?.address.primaryAddress]
    }
  }

  const isDisabledFields = (): boolean => {
    return disabled && !createCard
  }

  const isRetireDisabled = originalPosition.person === null

  const isfilledPositionFields = (): string[] => {
    const failedFields: string[] = []
    if (position.title === '') {
      failedFields.push('Title')
    }
    if (position.titleLocal === '') {
      failedFields.push('Local Title')
    }
    if (position.typeId === 0 || position.typeId == null) {
      failedFields.push('Position Type')
    }
    if (position.person?.firstName === '') {
      failedFields.push('Occupant\'s First Name')
    }
    if (position.person?.lastName === '' && !isEmptyLastNameAllowed(customBehaviourValues, originalPosition.countryId)) {
      failedFields.push('Occupant\'s Last Name')
    }
    return failedFields
  }
  const updateAddress = (newAddress: PersonAddress): void => {
    setPosition((value) => ({ ...value, address: newAddress }))
  }

  const updateRegion = (newRegion: Region | null): void => {
    setPosition((value) => ({ ...value, regionId: newRegion?.id }))
  }

  const updatePerson = async (newPerson: Person): Promise<void> => {
    setPosition((position) => ({ ...position, person: newPerson }))
    if (!createCard) {
      setLoading(true)
      getAddressFromOrg(originalPosition.organization.id).then(updateAddress).finally(() => { setLoading(false) })
    }
  }

  const handleFieldChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
    setPosition((position) => ({
      ...position,
      [event.target.name]: event.target.value
    }))
  }

  const handlePositionTypeChange = (newValue: number): void => {
    setPosition((position) => ({
      ...position,
      typeId: newValue
    }))
  }

  const handleDelete = (): void => {
    setLoading(true)
    void deletePosition(position.organization.id, position.id).then(() => {
      setLoading(false)
      void triggerReload()
    })
  }

  const handleOpen = (): void => {
    if (createCard) {
      clearPositionToAdd()
    } else {
      setOpenDeleteDialog(true)
    }
  }

  const handlerOpenRetireDialog = (): void => {
    setOpenRetireDialog(true)
  }

  const handlerClearSelection = (): void => {
    setPosition(originalPosition)
    setDisabled(true)
    setEditingCard(false)
    setPersonCreateMode(false)
  }

  const handlerCloseRetireDialog = (): void => {
    setOpenRetireDialog(false)
  }

  const handleRetirePerson = (): void => {
    setLoading(true)
    const personId: number = Number(position?.person?.id)
    void retirePersonFromPosition(position.organization.id, position.id, personId).then(() => {
      setLoading(false)
      void triggerReload()
    })
  }

  const handleClose = (): void => {
    setOpenDeleteDialog(false)
  }

  const prettierKeyNames: Record<string, string> = {
    id: 'ID',
    addressOne: 'Address Line 1',
    addressTwo: 'Address Line 2',
    city: 'City',
    state: 'State',
    country: 'Country',
    postalCode: 'Postal Code',
    mailingAddress: 'Mailing Address'
  }

  const isAllFieldsPrimaryFilled = (): string[] => {
    if (position.address?.primaryAddress != null) {
      const propertiesNotRequired = new Set<string>(['addressTwo', 'state'])
      const failedKeys: string[] = Object.entries((position.address.primaryAddress)).filter(
        ([key, value]) => !propertiesNotRequired.has(key) && (value === null || value === '')
      ).map(([key]) => prettierKeyNames[key] ?? key)
      return failedKeys
    } else {
      return ['Address']
    }
  }

  return (
        <Card key={`position-card-${index}`} sx={{ padding: 2 }}>
          <Box display={'flex'} justifyContent={'space-between'}>
            {displayOrgLink &&
                <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', pb: 2 }}>
                  <Typography key={originalPosition.organization.id}>@ {originalPosition.organization.id} - {originalPosition.organization.organizationName} ({originalPosition.organization?.type?.typeCode}) - {originalPosition.organization.topLevelOrg?.organizationName} ({originalPosition.organization.topLevelOrg?.type?.typeCode})</Typography>
                  <IconButtonWithTooltip color={'primary'} label={'Go to Org. Update Page'} onClick={() => { openNewTab(`/editOrganization/${originalPosition.organization.id}`) }} icon={<ArrowOutwardIcon/>}/>
                </Box>
            }
            <Box display={'flex'} flexDirection={'row'} pb={2} alignItems={'center'}>
              <LastUpdatedComponent value={position.lastUpdated}></LastUpdatedComponent>
              <Typography sx = {{ marginLeft: 1 }} variant={'caption'}>
                {`| Position from ${originalPosition.countryId != null ? countryLutValues.countries.find(country => country.id === originalPosition.countryId)?.country_desc ?? 'Unknown' : 'EU'}` }
              </Typography>
            </Box>
          </Box>
            <Grid container alignItems={'center'} spacing={1}>
                <Grid item xs={3}>
                    <FormControl fullWidth>
                        <TextField name={'title'} disabled={isDisabledFields()} label={'Title'} value={position.title} onChange={handleFieldChange}/>
                    </FormControl>
                </Grid>
                <Grid item xs={3}>
                    <FormControl fullWidth>
                        <TextField name={'titleLocal'} disabled={isDisabledFields()} label={'Local Title'} value={position.titleLocal} onChange={handleFieldChange}/>
                    </FormControl>
                </Grid>
                <Grid item xs={2}>
                    <FormControl fullWidth>
                        <InputLabel id={`selectPosType-${position.id}`}>Position Type</InputLabel>
                        <Select disabled={isDisabledFields() || loadingPositionLutvalues} onChange={(event) => { handlePositionTypeChange(Number(event.target.value)) }} value={position.typeId} labelId={`selectPosType-${position.id}`}
                                label={'Position Type'}>
                            {
                                positionLutValues.positionTypes.map((option) => (
                                    <MenuItem key={option.id} value={option.id}>
                                        {option.typeDescShort}
                                    </MenuItem>
                                ))}
                        </Select>
                    </FormControl>

                </Grid>
                <Grid item xs={3}>
                    <PersonAutocomplete<Person> person={position.person} setEditing={setEditingCard} editing={editingCard} createPerson={createPersonMode} setCreatePerson={setPersonCreateMode} updatePerson={(newPerson) => { void updatePerson(newPerson) }} editable={!disabled} filledPerson={originalPosition.person == null} countryId={position.countryId}/>
                </Grid>
                {!loading &&
                <Grid item xs={1}>
                  <Grid container alignItems={'center'} spacing={1}>
                    <Grid item xs={4}>
                        {isDisabledFields() &&
                      <Tooltip title="Edit Position">
                        <span>
                          <IconButton aria-label="Edit Position" color={'primary'} onClick={() => { setDisabled(false) }}>
                              <EditIcon/>
                          </IconButton>
                        </span>
                      </Tooltip>
                      }
                      {!isDisabledFields() &&
                        <Tooltip title="Save Position">
                          <span>
                            <IconButton aria-label="Save Position" color={'primary'} onClick={() => { handleSave() }}>
                                <SaveIcon/>
                            </IconButton>
                          </span>
                        </Tooltip>
                      }
                    </Grid>
                    <Grid item xs={4}>
                      {!disabled
                        ? (
                              <Tooltip title="Cancel Editing">
                               <span>
                                <IconButton aria-label="Cancel Occupant" color={'secondary'} onClick={() => { handlerClearSelection() }}>
                                 <CloseIcon/>
                                </IconButton>
                               </span>
                              </Tooltip>
                          )
                        : (
                              <Tooltip title="Retire Occupant">
                               <span>
                                 <IconButton disabled={isRetireDisabled} aria-label="Retire Occupant" color={'secondary'} onClick={() => { handlerOpenRetireDialog() }}>
                                  <HourglassEmptyIcon/>
                                 </IconButton>
                               </span>
                              </Tooltip>)
                      }
                    </Grid>
                    <Grid item xs={4}>
                      <Tooltip title="Delete Position">
                        <span>
                          <IconButton aria-label="Remove Position" color={'primary'} onClick={() => { handleOpen() }}>
                              <DeleteIcon/>
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Grid>
                    <Grid item xs={4}>
                      <CommentDialog
                        recordId={position.id}
                        recordType={'R'}
                        commentService={internationalCommentService}
                        lastUpdated={position.commentLastUpdated ?? null}
                        onSaved={(lastUpdated) => { setPosition((value) => ({ ...value, commentLastUpdated: lastUpdated })) }}
                      />
                    </Grid>
                  </Grid>
                </Grid>
                }
                {loading &&
                    <Grid item xs={1}>
                        <CircularProgress/>
                    </Grid>
                    }

            </Grid>
            <Typography marginLeft={1} variant={'caption'}>Address: </Typography>
           {(position.person !== null && position.address !== null) && <PersonAddressBlock disabled={isDisabledFields()} region={position.regionId != null ? { id: position.regionId, desc: '', countryId: originalPosition.countryId ?? null } : null} countryId={originalPosition.countryId ?? null} updateRegion={updateRegion} startExpanded={createCard} updateAddress ={updateAddress} personAddress={position.address}
                                                                                           index={index}/>}
            {(position.person == null || position.address == null) && <Typography marginLeft={1} variant={'caption'}>The position has no address.</Typography>}
            <AlertDialog
                onClose={handleClose}
                open={openDeleteDialog}
                onButtonClick={handleDelete}
                title='Would you like to delete this position?'
                contentText='Are you sure you want to delete this position? If you confirm, please click on "Confirm."'
                nameButtonCancel='Close'
                nameButtonConfirm='Confirm'
            />
             <AlertDialog
                onClose={handlerCloseRetireDialog}
                open={openRetireDialog}
                onButtonClick={handleRetirePerson}
                title='Would you like to retire this person from position?'
                contentText='Are you sure you want to retire this person? If you confirm, please click on "Confirm."'
                nameButtonCancel='Close'
                nameButtonConfirm='Confirm'
            />
        </Card>

  )
}
